/**
 * TaskService
 * Service for Task entity operations
 * Route: api/tasks (updated to RESTful plural convention)
 */
import { CrudService } from '../base/CrudService';
/**
 * TaskService
 * Handles task CRUD operations and task-specific queries
 * Uses /api/tasks route (TasksController)
 */
export class TaskService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? TaskService.DEFAULT_SERVICE_PATH,
            basePath: TaskService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== LIST VIEW ====================
    /**
     * Get tasks list view with optional filters
     * POST api/tasks/list-view
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get tasks list view with full response
     */
    listViewRaw(filter) {
        const endpoint = this.getEndpoint('list-view', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter);
    }
    // ==================== MY LIST VIEW ====================
    /**
     * Get current user's tasks list view
     * POST api/tasks/my-list-view
     */
    async myListView(filter) {
        const response = await this.myListViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get current user's tasks list view with full response
     */
    myListViewRaw(filter) {
        const endpoint = this.getEndpoint('my-list-view', 'my-list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter);
    }
    // ==================== GET VIEW ====================
    /**
     * Get task view by ID
     * GET api/tasks/{id}/view
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`Task ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get task view by ID with full response
     */
    getViewRaw(id) {
        const endpoint = this.getEndpoint('view', `${id}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== UPDATE STATUS ====================
    /**
     * Update task status
     * PATCH api/tasks/{id}/status
     */
    async updateStatus(id, statusId, progressStageId) {
        const response = await this.updateStatusRaw(id, { statusId, progressStageId });
        if (!response.data) {
            throw new Error(`Failed to update status for task ${id}`);
        }
        return response.data;
    }
    /**
     * Update task status with full response
     */
    updateStatusRaw(id, request) {
        const endpoint = this.getEndpoint('status', `${id}/status`);
        const url = this.buildUrl(endpoint);
        return this.http.patch(url, request);
    }
    // ==================== UPDATE PRIORITY ====================
    /**
     * Update task priority
     * PATCH api/tasks/{id}/priority
     * @param id - Task ID
     * @param priorityId - Priority ID to assign
     * @returns Updated task
     */
    async updatePriority(id, priorityId) {
        const response = await this.updatePriorityRaw(id, priorityId);
        if (!response.data) {
            throw new Error(`Failed to update priority for task ${id}`);
        }
        return response.data;
    }
    /**
     * Update task priority with full response
     * @param id - Task ID
     * @param priorityId - Priority ID to assign
     * @returns Full response with updated task
     */
    updatePriorityRaw(id, priorityId) {
        const endpoint = this.getEndpoint('priority', `${id}/priority`);
        const url = this.buildUrl(endpoint);
        return this.http.patch(url, { priorityId });
    }
    // ==================== UPDATE PROJECT ====================
    /**
     * Assign task to project (or remove assignment)
     * PATCH api/tasks/{id}/project
     * @param id - Task ID
     * @param projectId - Project ID (set to 0 or null to remove)
     * @returns Updated task
     */
    async updateProject(id, projectId) {
        const response = await this.updateProjectRaw(id, projectId);
        if (!response.data) {
            throw new Error(`Failed to update project for task ${id}`);
        }
        return response.data;
    }
    /**
     * Assign task to project with full response
     * @param id - Task ID
     * @param projectId - Project ID (set to 0 or null to remove)
     * @returns Full response with updated task
     */
    updateProjectRaw(id, projectId) {
        const endpoint = this.getEndpoint('project', `${id}/project`);
        const url = this.buildUrl(endpoint);
        return this.http.patch(url, { projectId });
    }
    // ==================== ASSIGN USER ====================
    /**
     * Assign user to task
     * POST api/tasks/{id}/users/{userId}
     * @param id - Task ID
     * @param userId - User ID to assign
     * @returns Created task assignment
     */
    async assignUser(id, userId) {
        const response = await this.assignUserRaw(id, userId);
        if (!response.data) {
            throw new Error(`Failed to assign user ${userId} to task ${id}`);
        }
        return response.data;
    }
    /**
     * Assign user to task with full response
     * @param id - Task ID
     * @param userId - User ID to assign
     * @returns Full response with created task assignment
     */
    assignUserRaw(id, userId) {
        const endpoint = this.getEndpoint('assign-user', `${id}/users/${userId}`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url);
    }
    // ==================== REMOVE USER ====================
    /**
     * Remove user from task
     * DELETE api/tasks/{id}/users/{userId}
     * @param id - Task ID
     * @param userId - User ID to remove
     * @returns Success status
     */
    async removeUser(id, userId) {
        const response = await this.removeUserRaw(id, userId);
        return response.data ?? false;
    }
    /**
     * Remove user from task with full response
     * @param id - Task ID
     * @param userId - User ID to remove
     * @returns Full response with success status
     */
    removeUserRaw(id, userId) {
        const endpoint = this.getEndpoint('remove-user', `${id}/users/${userId}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ==================== ADMIN LIST VIEW ====================
    /**
     * Get all tasks with admin filters
     * POST api/tasks/admin/list-view
     * @param filter - Optional filter criteria
     * @returns Array of task model views
     */
    async getAdminListView(filter) {
        const response = await this.getAdminListViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get all tasks with admin filters with full response
     * @param filter - Optional filter criteria
     * @returns Full response with task model views
     */
    getAdminListViewRaw(filter) {
        const endpoint = this.getEndpoint('admin-list-view', 'admin/list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter || {});
    }
    // ==================== GET MY TASKS ====================
    /**
     * Get current user's tasks
     * GET api/tasks/user/my-tasks
     * @returns Array of task model views for current user
     */
    async getMyTasks() {
        const response = await this.getMyTasksRaw();
        return response.data ?? [];
    }
    /**
     * Get current user's tasks with full response
     * @returns Full response with task model views
     */
    getMyTasksRaw() {
        const endpoint = this.getEndpoint('my-tasks', 'user/my-tasks');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== GET USER ASSIGNED TASKS ====================
    /**
     * Get tasks assigned to a specific user with optional filters
     * GET/POST api/tasks/user/{userId}/assigned
     * @param userId - User ID
     * @param filter - Optional filter criteria
     * @returns Array of task model views for specified user
     */
    async getUserAssignedTasks(userId, filter) {
        const response = await this.getUserAssignedTasksRaw(userId, filter);
        return response.data ?? [];
    }
    /**
     * Get tasks assigned to a specific user with full response
     * @param userId - User ID
     * @param filter - Optional filter criteria
     * @returns Full response with task model views
     */
    getUserAssignedTasksRaw(userId, filter) {
        const endpoint = this.getEndpoint('user-assigned-tasks', `user/${userId}/assigned`);
        const url = this.buildUrl(endpoint);
        if (filter) {
            return this.http.post(url, filter);
        }
        return this.http.get(url);
    }
    // ==================== GET MY CREATED TASKS ====================
    /**
     * Get tasks CREATED by current user (tasks I created)
     * Uses list-view endpoint with createdBy filter
     * @param filter - Optional filter criteria
     * @returns Array of task model views created by current user
     */
    async getMyCreatedTasks(filter) {
        const response = await this.getMyCreatedTasksRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get tasks CREATED by current user with full response
     * @param filter - Optional filter criteria
     * @returns Full response with task model views
     */
    getMyCreatedTasksRaw(filter) {
        // Use list-view endpoint with createdBy='current' to filter by creator
        // Backend will interpret 'current' as the authenticated user
        const endpoint = this.getEndpoint('list-view', 'list-view');
        const url = this.buildUrl(endpoint);
        const filterWithCreatedBy = {
            ...filter,
            createdBy: 'current',
        };
        return this.http.post(url, filterWithCreatedBy);
    }
    // ==================== GET MY TASKS FILTERED ====================
    /**
     * Get tasks ASSIGNED to current user with POST filter support
     * POST api/tasks/user/my-tasks
     * @param filter - Optional filter criteria
     * @returns Array of task model views assigned to current user
     */
    async getMyTasksFiltered(filter) {
        const response = await this.getMyTasksFilteredRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get tasks ASSIGNED to current user with full response
     * @param filter - Optional filter criteria
     * @returns Full response with task model views
     */
    getMyTasksFilteredRaw(filter) {
        const endpoint = this.getEndpoint('my-tasks-filtered', 'user/my-tasks');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter || {});
    }
}
TaskService.DEFAULT_SERVICE_PATH = 'tasks';
TaskService.BASE_PATH = 'api/tasks';
//# sourceMappingURL=TaskService.js.map